/**
 * Excel's worksheet name constraints (§9.7):
 * - Max 31 characters
 * - Forbidden characters: `: \ / ? * [ ]`
 */
const EXCEL_MAX_SHEET_NAME_LENGTH = 31;
const EXCEL_FORBIDDEN_SHEET_NAME_CHARS = /[:\\/?*[\]]/g;

/**
 * De-duplicate sheet names by appending `(2)`, `(3)`, ... to repeats.
 *
 * Must run **before** Excel sanitization (mapping §7.3, §9.7) — two long custom-tab names that
 * would truncate identically (e.g. `"Some long tab name A"` and `"Some long tab name B"` if both
 * exceed 31 chars before their disambiguating suffix) need their `(N)` markers in place before
 * any truncation strips them.
 */
export const dedupeSheetNames = (names: readonly string[]): string[] => {
	const seen = new Map<string, number>();
	return names.map((name) => {
		const count = seen.get(name) ?? 0;
		seen.set(name, count + 1);
		return count === 0 ? name : `${name} (${count + 1})`;
	});
};

/**
 * Truncate to Excel's 31-char limit and replace forbidden characters with `-` (mapping §9.7).
 *
 * Applied per name **after** `dedupeSheetNames` so the dedup `(N)` suffixes are preserved when
 * possible (they may themselves get truncated if the name is at the limit).
 */
export const sanitizeSheetName = (name: string): string => {
	return name.substring(0, EXCEL_MAX_SHEET_NAME_LENGTH).replace(EXCEL_FORBIDDEN_SHEET_NAME_CHARS, "-");
};

const EXCEL_RESERVED_SHEET_NAME = "history";
const EDGE_APOSTROPHES = /^'+|'+$/g;

/**
 * Tab names from text a user typed (custom shift time group names): sanitized, then made unique the
 * way Excel compares them — ignoring case and after the 31-character cut — and never `History` or a
 * name starting or ending with an apostrophe, which Excel refuses. Repeats get ` (2)`, ` (3)`, … within
 * the length limit.
 */
export const finalizeUserSheetNames = (rawNames: readonly string[]): string[] => {
	const taken = new Set<string>();
	return rawNames.map((raw) => {
		const stripped = sanitizeSheetName(raw).replace(EDGE_APOSTROPHES, "");
		// DEFAULT: a name made only of apostrophes leaves nothing Excel accepts; a dash keeps the tab.
		const base = stripped.length > 0 ? stripped : "-";
		let candidate = base;
		for (let copy = 2; taken.has(candidate.toLowerCase()) || candidate.toLowerCase() === EXCEL_RESERVED_SHEET_NAME; copy++) {
			const suffix = ` (${copy})`;
			candidate = `${base.substring(0, EXCEL_MAX_SHEET_NAME_LENGTH - suffix.length)}${suffix}`;
		}
		taken.add(candidate.toLowerCase());
		return candidate;
	});
};

/**
 * The standard pipeline: dedupe → sanitize. Returns a same-length array of safe sheet names.
 */
export const finalizeSheetNames = (rawNames: readonly string[]): string[] => {
	return dedupeSheetNames(rawNames).map(sanitizeSheetName);
};
